import { useLocation, useNavigate } from "react-router-dom";
import useExitPrompt from "../../../core/utils/UseExitPrompt";
import { useEffect, useRef, useState } from "react";
import { HorizontalOnlyContainer } from "../../component/HorizontalOnlyContainer";
import { GameViewModel } from "./GameViewModel";
import { Category } from "../../../domain/model/Category";
import { GameData, TeamTurns } from "../../../domain/model/Game";
import { Difficulty } from "../../../domain/model/Question";
import {
  alpha,
  Backdrop,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Zoom,
} from "@mui/material";
import { CustomImageView } from "../../component/CustomImageView";
import {
  COLOR_BLACK,
  COLOR_GOLD,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../data/colors/Colors";
import { CustomTextView } from "../../component/CustomTextView";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../../../core/utils/UseWindowDimensions";
import {
  CheckCircleOutline,
  CloseOutlined,
  ExitToAppRounded,
} from "@mui/icons-material";
import { getDifficultyInfo } from "../../../core/utils/Utils";
import { QuestionView } from "./question/QuestionView";
import { CustomPopoverView } from "../../component/CustomPopoverView";
import {
  provideGetAnswerByQuestionIdUseCase,
  provideGetRandomQuestionUseCase,
  provideQuestionGiveAnswerUseCase,
  provideUpdateGameUseCase,
} from "../../di/UseCasesModule";
import { ROUTE_ENDPOINTS } from "../../../data/constant/RouteConstants";
import { CustomAlertDialog } from "../../component/CustomAlertDialog";
import PlusIcon from "../../../assets/images/plus_icon.png";
import MinusIcon from "../../../assets/images/minus_icon.png";
import {
  Coordinate,
  RandomCharacterSpawner,
} from "../../component/RandomCharacterSpawner";

export function GameView() {
  const location = useLocation();
  const viewModel = GameViewModel(
    provideGetRandomQuestionUseCase(),
    provideGetAnswerByQuestionIdUseCase(),
    provideQuestionGiveAnswerUseCase(),
    provideUpdateGameUseCase(),
  );
  useExitPrompt(true);

  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  const { t } = useTranslation();
  useEffect(() => {
    if (location.state) {
      try {
        viewModel.init(location.state);
      } catch (error) {
        alert(error);
      }
    }
  }, []);

  const gameData = viewModel.gameData;

  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    window.history.pushState(null, "", location.pathname);

    const handlePopState = () => {
      window.history.pushState(null, "", location.pathname);
      navigate(ROUTE_ENDPOINTS.GAME, { state: location.state, replace: true });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, location.pathname]);

  const [showExitAlertDialog, setShowExitAlertDialog] = useState(false);

  useEffect(() => {
    if (viewModel.question && viewModel.answer) {
      setAnchorElPosition({
        top: 0,
        left: 0,
      });
    }
  }, [viewModel.question, viewModel.answer]);

  const lastAnswerWasCorrect = viewModel.lastAnswerWasCorrect;
  const backdropOpen = viewModel.lastAnswerWasCorrect != undefined;
  // This "remembers" the color during the fade-out
  const lastColorRef = useRef("transparent");

  // Update the ref only when the state is actually true or false
  if (lastAnswerWasCorrect === true) {
    lastColorRef.current = "rgba(76, 175, 80, 0.9)"; // Green
  } else if (lastAnswerWasCorrect === false) {
    lastColorRef.current = "rgba(244, 67, 54, 0.9)"; // Red
  }

  const ALLOWED_SPOTS: Coordinate[] = [
    { top: "40%", left: "5%" },
    { top: "40%", left: "25%" },
    { top: "40%", right: "42.5%" },
    { top: "40%", right: "25%" },
    { top: "40%", right: "5%" },
  ];

  return (
    <HorizontalOnlyContainer>
      <Box
        sx={{
          position: "relative", // Crucial for the spawner coordinates to anchor here
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {gameData ? (
          <Box>
            {/* MUI BACKDROP EFFECT */}
            <Backdrop
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: lastColorRef.current,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
              open={backdropOpen}
            >
              {lastAnswerWasCorrect !== undefined && (
                <>
                  <Zoom
                    in={backdropOpen}
                    style={{ transitionDelay: backdropOpen ? "100ms" : "0ms" }}
                  >
                    <div>
                      {viewModel.lastAnswerWasCorrect === true ? (
                        <CheckCircleOutline sx={{ fontSize: 120 }} />
                      ) : (
                        <CloseOutlined sx={{ fontSize: 120 }} />
                      )}
                    </div>
                  </Zoom>

                  <Zoom
                    in={backdropOpen}
                    style={{ transitionDelay: backdropOpen ? "300ms" : "0ms" }}
                  >
                    <div>
                      <CustomTextView
                        text={
                          viewModel.lastAnswerWasCorrect === true
                            ? t("correct_answer")
                            : t("wrong_answer")
                        }
                        variant="h2"
                        textColor={COLOR_WHITE}
                        fontWeight="bold"
                      />
                    </div>
                  </Zoom>
                </>
              )}
            </Backdrop>
            <Stack
              width={"100vw"}
              height={"100vh"}
              justifyContent={"space-between"}
              alignItems={"center"}
              overflow={"hidden"}
            >
              <Stack width={"100%"} bgcolor={COLOR_MAIN} p={3} height={"8vh"}>
                <TopBarView
                  gameData={gameData}
                  addOrDeductPointsFromATeam={
                    viewModel.addOrDeductPointsFromATeam
                  }
                  onExitButtonClicked={() => setShowExitAlertDialog(true)}
                />
              </Stack>
              <Stack
                width={"100vw"}
                alignItems={"center"}
                justifyContent={"center"}
                sx={{ px: 1, flexGrow: 1, minHeight: 0, py: 6 }}
              >
                <Container maxWidth={false} sx={{ height: "100%" }}>
                  <Grid
                    container
                    rowSpacing={isMobile ? 2 : 3}
                    columnSpacing={2}
                    sx={{
                      width: "100%",
                      height: "100%",
                      alignItems: "stretch",
                    }}
                  >
                    {gameData?.categories.map((category) => {
                      return (
                        <Grid
                          key={category.id}
                          size={4}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                          }}
                        >
                          <Stack
                            width={"100%"}
                            height={"100%"}
                            sx={{ flexGrow: 1, minHeight: 0 }}
                          >
                            <CategoryItemView
                              category={category}
                              onDifficultyClicked={(_, d) => {
                                viewModel.getRandomQuestion(category, d);
                              }}
                              teamTurn={gameData.currentTeamTurn}
                            />
                          </Stack>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Container>
              </Stack>
            </Stack>

            <CustomPopoverView
              onClose={() => {}}
              anchorElPosition={anchorElPosition}
              transitionDuration={500}
              layout={
                <QuestionView
                  onClosePopover={({ who }) => {
                    setAnchorElPosition(null);
                    viewModel.OnQuestionAnswered({
                      who,
                    });
                  }}
                  gameDataInput={gameData}
                  questionInput={viewModel.question}
                  answerInput={viewModel.answer}
                />
              }
            />

            <CustomAlertDialog
              open={showExitAlertDialog}
              title={t("exit_game")}
              description={t("are_you_sure_you_want_exit_game")}
              handlePositiveButtonClick={() => {
                setShowExitAlertDialog(false);
                navigate(ROUTE_ENDPOINTS.HOME_PLAY, { replace: true });
              }}
              handleNegativeButtonClick={() => {
                setShowExitAlertDialog(false);
              }}
            />
          </Box>
        ) : (
          <Stack
            width={"100vw"}
            height={"100vh"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={1}
          >
            GAME WAS EXITED UNEXPECTEDLY
            <IconButton
              sx={{
                background: "red",
                borderRadius: 50,
                width: "2vw",
                height: "2vw",
                "&:hover": {
                  backgroundColor: alpha(COLOR_BLACK, 0.1),
                  "@media (hover: none)": {
                    backgroundColor: "red",
                  },
                },
              }}
              onClick={() => {
                navigate(ROUTE_ENDPOINTS.HOME_PLAY, { replace: true });
              }}
            >
              <ExitToAppRounded
                sx={{ color: COLOR_WHITE, width: "1vw", height: "1vw" }}
              />
            </IconButton>
          </Stack>
        )}
      </Box>

      {!isMobile && <RandomCharacterSpawner allowedLocations={ALLOWED_SPOTS} />}
    </HorizontalOnlyContainer>
  );
}

function TopBarView({
  gameData,
  addOrDeductPointsFromATeam,
  onExitButtonClicked,
}: {
  gameData: GameData;
  addOrDeductPointsFromATeam: (isFirstTeam: boolean, add: boolean) => void;
  onExitButtonClicked: () => void;
}) {
  const { height } = useWindowDimensions();
  const isMobile = height < 700;

  return (
    <Stack height={"100%"} position={"relative"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        width={"100%"}
        justifyContent={"space-between"}
        height={"100%"}
      >
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomTextView
              text={gameData.firstTeamName}
              textColor={
                gameData.currentTeamTurn == TeamTurns.teamA
                  ? COLOR_GOLD
                  : "lightGray"
              }
              variant={isMobile ? "body2" : "h4"}
              fontWeight={
                gameData.currentTeamTurn == TeamTurns.teamA ? "bold" : "normal"
              }
            />
            <CustomTextView
              text={gameData.firstTeamScore.toString()}
              textColor={COLOR_WHITE}
              variant={isMobile ? "h6" : "h4"}
              fontWeight={"bold"}
            />
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(true, false);
              }}
              isPlus={false}
            />
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(true, true);
              }}
              isPlus
            />
          </Stack>
        </Stack>

        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Stack direction={"row"} alignItems={"center"} gap={0.5}>
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(false, true);
              }}
              isPlus
            />
            <PlusMinusButtonView
              onClicked={() => {
                addOrDeductPointsFromATeam(false, false);
              }}
              isPlus={false}
            />
          </Stack>
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomTextView
              text={gameData.secondTeamScore.toString()}
              textColor={COLOR_WHITE}
              variant={isMobile ? "h6" : "h4"}
              fontWeight={"bold"}
            />
            <CustomTextView
              text={gameData.secondTeamName}
              textColor={
                gameData.currentTeamTurn == TeamTurns.teamB
                  ? COLOR_GOLD
                  : "lightGray"
              }
              variant={isMobile ? "body2" : "h4"}
              fontWeight={
                gameData.currentTeamTurn == TeamTurns.teamB ? "bold" : "normal"
              }
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={1}
        justifyContent={"center"}
        position={"absolute"}
        alignSelf={"center"}
        height={"100%"}
      >
        <IconButton
          sx={{
            background: "red",
            borderRadius: 50,
            width: "3vw",
            height: "3vw",
            "&:hover": {
              backgroundColor: alpha(COLOR_WHITE, 0.5),
              "@media (hover: none)": {
                backgroundColor: "red",
              },
            },
          }}
          onClick={onExitButtonClicked}
        >
          <ExitToAppRounded
            sx={{ color: COLOR_WHITE, width: "2vw", height: "2vw" }}
          />
        </IconButton>
      </Stack>
    </Stack>
  );
}

function PlusMinusButtonView({
  isPlus,
  onClicked,
}: {
  isPlus: boolean;
  onClicked: () => void;
}) {
  return (
    <IconButton
      onClick={onClicked}
      sx={{
        background: COLOR_BLACK,
        borderRadius: 50,
        width: "2.5vw",
        height: "2.5vw",
        "&:hover": {
          backgroundColor: alpha(COLOR_WHITE, 0.6),
          "@media (hover: none)": {
            backgroundColor: COLOR_BLACK,
          },
        },
      }}
    >
      <CustomImageView
        src={isPlus ? PlusIcon : MinusIcon}
        width={"1.5vw"}
        height={"1.5vw"}
      />
    </IconButton>
  );
}

function CategoryItemView({
  category,
  teamTurn,
  onDifficultyClicked,
}: {
  category: Category;
  teamTurn: TeamTurns;
  onDifficultyClicked: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    difficulty: Difficulty,
    index: number,
  ) => void;
}) {
  const currentTurn = teamTurn;
  const { height } = useWindowDimensions();
  const isMobile = height < 700;
  const { t } = useTranslation();

  return (
    <Stack
      position={"relative"}
      width={"100%"}
      height={"100%"}
      alignItems={"center"}
      justifyContent={"center"}
      sx={{ flexGrow: 1, minHeight: 0 }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        width={"100%"}
        height={"100%"}
        justifyContent={"center"}
        spacing={0}
        sx={{ minHeight: 0 }}
      >
        {Object.values(TeamTurns).map((turn, teamIndex) => (
          <Stack
            key={turn}
            alignItems={"center"}
            width={"100%"}
            spacing={height < 700 ? "1.2vh" : "1.2vmax"}
            sx={{ flex: 1, minWidth: 0, minHeight: 0 }}
          >
            {Object.values(Difficulty).map((difficulty, diffIndex) => {
              const index = teamIndex * 3 + diffIndex; // 0..5
              const visible =
                category.pointsHashmap.get(turn)?.get(difficulty) === false;
              const disabled = currentTurn !== turn;
              return (
                <CategoryDifficultyButtonView
                  key={`${turn}-${difficulty}`}
                  title={getDifficultyInfo(
                    difficulty,
                    t,
                    category.schoolTypes,
                  ).point.toString()}
                  isForFirstTeam={turn === TeamTurns.teamA}
                  disabled={disabled}
                  onClicked={(e) => onDifficultyClicked(e, difficulty, index)}
                  visible={visible}
                />
              );
            })}
          </Stack>
        ))}
      </Stack>
      <Stack position={"absolute"} zIndex={1} width={"60%"}>
        <Stack
          width={"100%"}
          alignItems={"center"}
          sx={{
            border: "solid 4px " + COLOR_MAIN,
            borderRadius: 4,
            boxShadow: 20,
          }}
          bgcolor={COLOR_WHITE}
        >
          <CustomImageView
            src={category.imageUrl}
            width={"100%"}
            height={isMobile ? "10vw" : "13vw"}
            scale="contain"
            props={{
              borderRadius: 3.5,
              bgcolor: COLOR_WHITE,
            }}
          />
          <Stack
            sx={{
              background: COLOR_MAIN,
              width: "100%",
              alignItems: "center",
              borderRadius: 2,
              p: 0.2,
            }}
          >
            <CustomTextView
              text={category.title}
              textColor={COLOR_WHITE}
              fontWeight="bold"
              variant={height < 700 ? "body1" : "h5"}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}

function CategoryDifficultyButtonView({
  title,
  disabled,
  isForFirstTeam,
  visible,
  onClicked,
}: {
  title: string;
  disabled: boolean;
  isForFirstTeam: boolean;
  visible: boolean;
  onClicked: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) {
  const { height } = useWindowDimensions();
  const isMobile = height < 700;
  const borderRadius = 50;
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const topAndBottomLeftBorderRadius = isForFirstTeam
    ? isRtl
      ? 0
      : borderRadius
    : isRtl
      ? borderRadius
      : 0;

  const topAndBottomRightBorderRadius = isForFirstTeam
    ? isRtl
      ? borderRadius
      : 0
    : isRtl
      ? 0
      : borderRadius;
  return (
    <Button
      disabled={disabled}
      onClick={onClicked}
      fullWidth
      variant="contained"
      sx={{
        borderTopLeftRadius: topAndBottomLeftBorderRadius,
        borderBottomLeftRadius: topAndBottomLeftBorderRadius,
        borderTopRightRadius: topAndBottomRightBorderRadius,
        borderBottomRightRadius: topAndBottomRightBorderRadius,
        background: COLOR_MAIN,
        "&:hover": {
          backgroundColor: alpha(COLOR_MAIN, 0.5),
          "@media (hover: none)": {
            backgroundColor: COLOR_MAIN,
          },
        },
        p: height < 700 ? 0.8 : "0.8vmax",
        visibility: visible ? undefined : "hidden",
      }}
    >
      <Stack width={"100%"} alignItems={isForFirstTeam ? "start" : "end"}>
        <CustomTextView
          text={title}
          variant={height < 700 ? undefined : "h5"}
          fontSize={14}
          textColor={COLOR_WHITE}
          fontWeight="bold"
        />
      </Stack>
    </Button>
  );
}
